// Lightweight global fetch interceptor that attaches a Bearer token to any
// request hitting our own API. The token is stored in localStorage by
// AuthCallback after a successful Google OAuth round-trip. We keep the
// existing httpOnly cookie path for browsers that allow cross-domain
// cookies (most desktop Chrome installs) and fall back to this header for
// browsers that don't (notably Safari on iOS with Intelligent Tracking
// Prevention, which silently drops third-party cookies even when
// SameSite=None; Secure is set).
//
// Why patch `window.fetch` instead of refactoring every callsite? There
// are ~120 `fetch(...)` calls scattered across the React app. Wrapping
// fetch once at boot makes the change invisible to every consumer.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_PREFIX = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'kenscho.session_token';

export function getSessionToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; }
  catch { return ''; }
}

export function setSessionToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private-mode quotas, etc. — ignore */ }
}

export function clearSessionToken() { setSessionToken(''); }

let installed = false;

export function installFetchAuth() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = function patchedFetch(input, init) {
    try {
      const url = typeof input === 'string' ? input : (input?.url || '');
      // Only attach to our API. Don't leak the token to third-party hosts.
      if (API_PREFIX && url.startsWith(API_PREFIX)) {
        const token = getSessionToken();
        if (token) {
          const headers = new Headers(init?.headers || (typeof input !== 'string' ? input.headers : undefined));
          // Don't clobber a caller-supplied Authorization header.
          if (!headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
          }
          init = { ...(init || {}), headers };
        }
      }
    } catch { /* never break the fetch chain */ }
    return original(input, init);
  };
}
